/**
 * Rendu (palier A) : chaîne HDR (tone mapping ACES, bloom, vignette, anticrénelage, étalonnage par zone), ombres en
 * cascade du soleil sur les objets solides, occlusion ambiante à l'écran, soleil visible avec reflet d'objectif.
 * Réglages : art.json → render ; niveau selon la qualité choisie dans les options.
 *
 * Objets solides : maillages marqués par `markSolid` (modèles, forteresse, sol). Ce sont aussi les seuls à porter
 * des ombres et à masquer le reflet d'objectif.
 */
import {
  CascadedShadowGenerator,
  Color3,
  Color4,
  ColorCurves,
  DefaultRenderingPipeline,
  DynamicTexture,
  ImageProcessingConfiguration,
  LensFlare,
  LensFlareSystem,
  Mesh,
  MeshBuilder,
  SSAO2RenderingPipeline,
  StandardMaterial,
  type AbstractMesh,
  type Camera,
  type DirectionalLight,
  type InstancedMesh,
  type Scene,
} from '@babylonjs/core';
import type { ArtData, ZoneData } from '../data';
import type { Quality } from '../ui/settings';

interface QualityLevel {
  bloom: boolean;
  msaa: number;
  fxaa: boolean;
  shadows: { size: number; cascades: number } | null;
  ssao: boolean;
  flare: boolean;
}

const LEVELS: Record<Quality, QualityLevel> = {
  low: { bloom: false, msaa: 1, fxaa: true, shadows: null, ssao: false, flare: false },
  medium: { bloom: true, msaa: 1, fxaa: true, shadows: { size: 1024, cascades: 2 }, ssao: false, flare: true },
  high: { bloom: true, msaa: 4, fxaa: false, shadows: { size: 2048, cascades: 3 }, ssao: false, flare: true },
  epic: { bloom: true, msaa: 4, fxaa: false, shadows: { size: 2048, cascades: 4 }, ssao: true, flare: true },
};

/** Distance du disque solaire (dans la sphère du ciel, devant les étoiles). */
const SUN_DISTANCE = 18000;

/**
 * Marque un maillage solide : il porte et reçoit les ombres, masque le reflet d'objectif. Pour une instance, la
 * réception des ombres se règle sur le maillage source (Babylon l'ignore sur l'instance).
 */
export function markSolid(mesh: AbstractMesh): void {
  if (mesh.getTotalVertices() === 0) return;
  mesh.metadata = { ...(mesh.metadata ?? {}), solid: true };
  const source = (mesh as InstancedMesh).sourceMesh;
  if (source) source.receiveShadows = true;
  else mesh.receiveShadows = true;
}

export const isSolid = (mesh: AbstractMesh): boolean => Boolean(mesh.metadata?.solid) && mesh.isEnabled() && mesh.isVisible;

export class RenderFx {
  private readonly pipeline: DefaultRenderingPipeline;
  private ssao: SSAO2RenderingPipeline | null = null;
  private shadows: CascadedShadowGenerator | null = null;
  private shadowKey = '';
  private readonly sun: Mesh;
  private readonly sunMaterial: StandardMaterial;
  private flare: LensFlareSystem | null = null;
  private zoneGrade: ZoneData['grade'];

  constructor(
    private readonly scene: Scene,
    private readonly cameras: Camera[],
    private readonly key: DirectionalLight,
    private readonly art: ArtData,
  ) {
    const r = art.render;
    this.pipeline = new DefaultRenderingPipeline('main', true, scene, cameras);
    const ip = this.pipeline.imageProcessing;
    this.pipeline.imageProcessingEnabled = true;
    ip.toneMappingEnabled = true;
    ip.toneMappingType = ImageProcessingConfiguration.TONEMAPPING_ACES;
    ip.vignetteEnabled = r.vignette > 0;
    ip.vignetteWeight = r.vignette;
    ip.vignetteColor = new Color4(0, 0, 0, 0);
    ip.vignetteBlendMode = ImageProcessingConfiguration.VIGNETTEMODE_MULTIPLY;
    ip.colorCurvesEnabled = true;
    ip.colorCurves = new ColorCurves();
    this.pipeline.bloomThreshold = r.bloom.threshold;
    this.pipeline.bloomWeight = r.bloom.weight;
    this.pipeline.bloomKernel = r.bloom.kernel;
    this.pipeline.bloomScale = r.bloom.scale;

    // Soleil : disque lumineux (dégradé radial) dans la direction opposée à la lumière principale.
    this.sunMaterial = new StandardMaterial('sun', scene);
    this.sunMaterial.disableLighting = true;
    this.sunMaterial.emissiveTexture = radialTexture(scene, 'sun-disc', [[0, 1], [0.18, 1], [0.32, 0.55], [1, 0]]);
    this.sunMaterial.opacityTexture = this.sunMaterial.emissiveTexture;
    this.sunMaterial.backFaceCulling = false;
    this.sun = MeshBuilder.CreatePlane('sun', { size: r.sun.size }, scene);
    this.sun.material = this.sunMaterial;
    this.sun.billboardMode = Mesh.BILLBOARDMODE_ALL;
    this.sun.infiniteDistance = true;
    this.sun.isPickable = false;
    this.sun.applyFog = false;
    this.placeSun();
  }

  /** Niveau de rendu selon la qualité choisie ; « flashs réduits » adoucit le bloom. */
  applyQuality(quality: Quality, reducedFlashes: boolean): void {
    const level = LEVELS[quality];
    const r = this.art.render;
    this.pipeline.samples = level.msaa;
    this.pipeline.fxaaEnabled = level.fxaa;
    this.pipeline.bloomEnabled = level.bloom;
    this.pipeline.bloomWeight = r.bloom.weight * (reducedFlashes ? 0.5 : 1);
    this.setShadows(level.shadows);
    this.setSsao(level.ssao);
    this.setFlare(level.flare);
    this.applyGrade();
  }

  /** Étalonnage et teinte du soleil propres à la zone. */
  setZone(zone: ZoneData): void {
    this.zoneGrade = zone.grade;
    const c = zone.keyColor;
    this.sunMaterial.emissiveColor = new Color3(c[0], c[1], c[2]).scale(this.art.render.sun.intensity);
    this.placeSun();
    this.applyGrade();
  }

  private applyGrade(): void {
    const r = this.art.render;
    const g = this.zoneGrade;
    const ip = this.pipeline.imageProcessing;
    ip.exposure = r.exposure * (g?.exposure ?? 1);
    ip.contrast = r.contrast;
    const curves = ip.colorCurves!;
    curves.globalSaturation = r.saturation + (g?.saturation ?? 0);
    curves.globalHue = g?.hue ?? 0;
    curves.globalDensity = g?.density ?? 0;
  }

  /** Le soleil suit la direction de la lumière principale (à l'infini). */
  private placeSun(): void {
    const d = this.key.direction.normalizeToNew();
    this.sun.position.copyFrom(d.scale(-SUN_DISTANCE));
  }

  private setShadows(spec: QualityLevel['shadows']): void {
    const key = spec ? `${spec.size}x${spec.cascades}` : '';
    if (key === this.shadowKey) return;
    this.shadowKey = key;
    this.shadows?.dispose();
    this.shadows = null;
    if (!spec || !CascadedShadowGenerator.IsSupported) return;
    const r = this.art.render.shadows;
    const csm = new CascadedShadowGenerator(spec.size, this.key);
    csm.numCascades = spec.cascades;
    csm.shadowMaxZ = r.maxZ;
    csm.lambda = r.lambda;
    csm.stabilizeCascades = true;
    csm.cascadeBlendPercentage = 0.08;
    csm.depthClamp = true;
    csm.darkness = r.darkness;
    csm.bias = 0.004;
    csm.normalBias = 0.02;
    csm.filteringQuality = spec.size >= 2048 ? CascadedShadowGenerator.QUALITY_MEDIUM : CascadedShadowGenerator.QUALITY_LOW;
    const map = csm.getShadowMap();
    if (map) map.renderListPredicate = (mesh) => isSolid(mesh);
    this.shadows = csm;
  }

  private setSsao(enabled: boolean): void {
    if (enabled === Boolean(this.ssao)) return;
    if (!enabled) {
      this.ssao?.dispose();
      this.ssao = null;
      return;
    }
    if (!SSAO2RenderingPipeline.IsSupported) return;
    const s = this.art.render.ssao;
    const ssao = new SSAO2RenderingPipeline('ssao', this.scene, { ssaoRatio: 0.5, blurRatio: 1 }, this.cameras);
    ssao.radius = s.radius;
    ssao.totalStrength = s.strength;
    ssao.maxZ = s.maxZ;
    ssao.minZAspect = 0.5;
    ssao.samples = 16;
    ssao.expensiveBlur = true;
    this.ssao = ssao;
  }

  private setFlare(enabled: boolean): void {
    if (enabled === Boolean(this.flare)) return;
    if (!enabled) {
      this.flare?.dispose();
      this.flare = null;
      return;
    }
    const strength = this.art.render.sun.flare;
    if (strength <= 0) return;
    const system = new LensFlareSystem('sun-flare', this.sun, this.scene);
    system.meshesSelectionPredicate = (mesh) => isSolid(mesh);
    const glow = radialDataUrl([[0, 1], [0.25, 0.45], [1, 0]]);
    const ring = radialDataUrl([[0, 0], [0.7, 0], [0.82, 0.6], [0.9, 0], [1, 0]]);
    // Reflets discrets le long de l'axe soleil → centre de l'écran (tailles en part de l'écran).
    const flares: [number, number, Color3, string][] = [
      [0.07, 0, new Color3(1, 0.95, 0.85), glow],
      [0.03, 0.4, new Color3(0.5, 0.65, 1), glow],
      [0.06, 0.7, new Color3(0.6, 0.5, 1), ring],
      [0.02, 0.9, new Color3(1, 0.7, 0.4), glow],
      [0.1, 1.3, new Color3(0.35, 0.55, 0.9), ring],
    ];
    for (const [size, position, color, url] of flares) new LensFlare(size, position, color.scale(strength), url, system);
    this.flare = system;
  }

  get shadowsEnabled(): boolean {
    return Boolean(this.shadows);
  }
}

/** Dégradé radial blanc (points [rayon 0..1, opacité]) en texture. */
function radialTexture(scene: Scene, name: string, stops: [number, number][]): DynamicTexture {
  const tex = new DynamicTexture(name, { width: 256, height: 256 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  paintRadial(ctx, 256, stops);
  tex.update();
  tex.hasAlpha = true;
  return tex;
}

function radialDataUrl(stops: [number, number][]): string {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 128;
  paintRadial(canvas.getContext('2d')!, 128, stops);
  return canvas.toDataURL('image/png');
}

function paintRadial(ctx: CanvasRenderingContext2D, size: number, stops: [number, number][]): void {
  const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
  for (const [at, alpha] of stops) g.addColorStop(at, `rgba(255,255,255,${alpha})`);
  ctx.clearRect(0, 0, size, size);
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, size, size);
}
